import { Injectable } from '@angular/core';
import { capitalize, find, get } from 'lodash';

import { TestListEntity } from '../../../services/test-list/test-list.model.factory';
import {
  TEST_STATE,
  TestMode,
  TestTypeDisplayValue,
  TestTypeValue,
  TestModeLabel
} from '../../../services/test-list/types';
import { AGENT_MODEL } from '../../device/device';
import { DateFormatterService } from '../../date.formatter/date.formatter.service';
import { ApplicationsService } from '../../application/applications.service';
import { Application } from '../../application/applications.model.factory';
import { TestListService } from '../../../services/test-list/test-list.service';
import { DevicesService } from '../../device/devices.service';
import { UnmanagedDevicesService } from '../../unmanaged-devices/unmanaged-devices.service';
export enum ACTION_NAME {
  START = 'Start',
  STOP = 'Stop',
  EDIT = 'Edit',
  CLONE = 'Clone',
  VIEW_REPORTS = 'View Reports',
  VIEW_RESULTS = 'View Results',
  VIEW_RUNNING = 'View Running',
  MONITOR_REFLECTOR = 'Monitor Reflector',
  REMOVE = 'Remove'
}

@Injectable({
  providedIn: 'root'
})
export class TestListTransformationService {
  constructor(
    private readonly dateFormatter: DateFormatterService,
    private readonly applicationService: ApplicationsService,
    private readonly testListService: TestListService,
    private readonly deviceService: DevicesService,
    private readonly unmanagedDeviceService: UnmanagedDevicesService
  ) {}

  getTestNameInfo(entity: TestListEntity): { label: string; sublabel: string } {
    const customerInfo = entity.getCustomer()?.name
      ? entity.getCustomer()?.contact
        ? entity.getId() + '/' + entity.getCustomer()?.name + '/' + entity.getCustomer().contact
        : entity.getId() + '/' + entity.getCustomer().name
      : entity.getId();
    return {
      label: entity.getName(),
      sublabel: customerInfo
    };
  }

  getMonitorTestType(entity: TestListEntity): {
    type: string;
    label: string;
    sublabel: string;
  } {
    return {
      type: entity.getTestType(),
      label: entity.getLabelForTestType(entity.getTestType()),
      sublabel: entity.getLayerLabel() || entity.getModeLabel()
    };
  }

  // eslint-disable-next-line
  getAgentInfo(entity: any, destination: any): any {
    let result;
    if (!destination) {
      result = {
        label: entity.getOriginatorAgentAlias(),
        sublabel: entity.getTestList()?.phyName,
        sublabel1: this.getAgentModelLabel(entity.getModelLabel()),
        iconType: this.getAgentModelLabel(entity.getModelLabel())
      };
    } else {
      if (entity.getSecondaryAgent()) {
        result = {
          label: entity.getDestinationAgentAlias(),
          sublabel: entity.getSecondaryAgent()?.phyName,
          sublabel1: this.getAgentModelLabel(entity.getSecondaryModelLabel()),
          iconType: this.getAgentModelLabel(entity.getSecondaryModelLabel())
        };
      } else if (!entity.isAgentBased()) {
        const appData: Application = this.applicationService.getApplicationById(
          entity.getDestinationApplicationId()
        );
        result = {
          label: appData?.getName(),
          url: appData?.getUrl(),
          icon: 'default_app_icon'
        };
      } else if (
        (entity.getTestType() === TestTypeValue.TRUESPEED ||
          entity.getTestType() === TestTypeValue.UDP) &&
        entity.getModeLabel() === TestModeLabel.CLIENT_TO_MA
      ) {
        const isTestCreatedByInstrument = this.testListService.isTestCreatedByInstrument(entity);
        result = {
          label: isTestCreatedByInstrument ? 'Instrument' : 'PC-Client',
          url: '',
          iconType: isTestCreatedByInstrument ? AGENT_MODEL.MTS_5800V2 : 'pc-client'
        };
      } else {
        //MV-24894	- show loopback destination device
        const secondaryData = entity
          .getTestListData()
          ?.tpInfo?.filter(data => data.type === 'secondary');
        if (secondaryData?.length > 0) {
          if (secondaryData.length === 1) {
            const deviceId = secondaryData[0].id;
            const destinationDevice =
              secondaryData[0].model === 'unmanaged'
                ? this.unmanagedDeviceService.findDeviceByID(deviceId)
                : this.deviceService.findDeviceByID(deviceId);
            result = {
              label: destinationDevice?.getDeviceName() || secondaryData[0].ip,
              sublabel1: this.getAgentModelLabel(secondaryData[0].model),
              iconType: this.getAgentModelLabel(secondaryData[0].model)
            };
          } else {
            result = {
              label: 'multiple'
            };
          }
        }
      }
    }

    return result;
  }

  getTestStatus(entity: TestListEntity): {
    icon: string;
    label: string;
    iconclass: string;
    sublabel: string;
  } {
    const message = entity?.getTestStateMessage()?.replace(/\\/g, '') || '';
    return {
      icon: 'mdi-circle-slice-8',
      label: capitalize(entity.getTestStatus()),
      iconclass: this.assignClassforState(entity.getTestStatus()),
      sublabel:
        entity.getTestStatus() === 'cancelled'
          ? 'Cancelled by user'
          : entity.getTestStatus() === 'aborted' ||
            entity.getTestStatus() === 'failed' ||
            entity.getTestStatus() === 'lost'
          ? capitalize(message)
          : ''
    };
  }

  assignClassforState(state: string): string {
    if (
      state === 'stopping' ||
      state === 'starting' ||
      state === 'queued' ||
      state === 'scheduled'
    ) {
      return 'warning';
    } else if (state === 'running') {
      return 'good';
    } else if (state === 'stopped' || state === 'finished') {
      return 'none';
    } else if (state === 'unknown' || state === 'failed') {
      return 'critical';
    }
    return 'critical';
  }

  // eslint-disable-next-line
  getTestStateActions(data: TestListEntity): any[] {
    const state = data.getTestStatus();
    const testType = data.getTestType();
    const message = get(data.getTestStateMessage(), 'Unknown');
    const schedule = this.getSchedulingInfo(data);
    const testId = data.getId();
    const hideReport =
      testType === TestTypeValue.SERVICE_CONTINUITY ||
      testType === TestTypeValue.VOIP ||
      testType === TestTypeValue.MULTISTEP ||
      testType === TestTypeValue.TWAMP ||
      testType === TestTypeValue.Y1731 ||
      testType === TestTypeValue.TWAMP_REFLECTOR ||
      testType === TestTypeValue.TWAMP_LIGHT_REFLECTOR;

    const hideResults = testId && state !== TEST_STATE.RUNNING;
    const disableStart =
      this.getExpiryDate(data).expired === 'expired' ||
      data.getAgentMode() === TestMode.CLIENT_TO_MA;
    const disableStop =
      data.getAgentMode() === TestMode.CLIENT_TO_MA &&
      (state === TEST_STATE.RUNNING ||
        state === TEST_STATE.SCHEDULED ||
        state === TEST_STATE.READY);
    const result = [];
    const actions = [
      {
        id: 'start',
        option: 'Start',
        label: ACTION_NAME.START,
        disable: disableStart,
        iconName: 'mdi-play-box',
        isDefaultAction: false
      },
      {
        id: 'stop',
        option: 'Stop',
        label: ACTION_NAME.STOP,
        disable: disableStop,
        iconName: 'mdi-stop',
        isDefaultAction: false
      },
      {
        id: 'edit',
        option: 'Edit',
        label: ACTION_NAME.EDIT,
        disable: this.testListService.isEditDisable(data),
        iconName: 'mdi-file-document-edit',
        isDefaultAction: false
      },
      {
        id: 'clone',
        option: 'Clone',
        label: ACTION_NAME.CLONE,
        disable: this.testListService.isCloneDisabled(data),
        iconName: 'mdi-content-copy',
        isDefaultAction: false
      },
      // {
      //   id: 'view',
      //   option: 'View',
      //   label: ACTION_NAME.VIEW_RUNNING,
      //   disable: true,
      //   iconName: 'mdi-play-speed',
      //   isDefaultAction: false
      // },
      {
        id: 'view',
        option: 'View',
        label: ACTION_NAME.VIEW_RESULTS,
        disable: hideResults,
        iconName: 'mdi-file-table-outline',
        isDefaultAction: false
      },
      {
        id: 'view_reports',
        option: 'View_reports',
        label: ACTION_NAME.VIEW_REPORTS,
        disable: hideReport,
        iconName: 'mdi-chart-bar',
        isDefaultAction: false
      },
      {
        id: 'remove',
        option: 'Remove',
        label: 'Remove',
        disable: this.testListService.isTestStarted(data),
        iconName: 'mdi-delete',
        isDefaultAction: false
      }
    ];

    if (!state) {
      return [actions];
    }
    let optionID = ['start'];
    if (testType === TestTypeValue.TRUESPEED) {
      optionID = ['start', 'edit'];
    }
    switch (state) {
      case TEST_STATE.STARTING: {
        result.push({
          label: 'STARTING',
          labelMessage: capitalize(message),
          result: 'warning',
          type: '',
          actions: this.setDisableAndDefaultAction(optionID, actions, state, schedule, testType)
        });
        break;
      }
      case TEST_STATE.QUEUED: {
        result.push({
          label: 'QUEUED',
          labelMessage: capitalize(message),
          result: 'warning',
          type: '',
          actions: this.setDisableAndDefaultAction(optionID, actions, state, schedule, testType)
        });
        break;
      }
      case TEST_STATE.RUNNING: {
        result.push({
          label: 'RUNNING',
          labelMessage: capitalize(message),
          result: 'good',
          type: '',
          actions: this.setDisableAndDefaultAction(optionID, actions, state, schedule, testType)
        });
        break;
      }
      case TEST_STATE.ABORTED: {
        result.push({
          label: 'FINISHED',
          labelMessage:
            message === 'stopped'
              ? 'Test has finished and is no longer running'
              : capitalize(message),
          result: 'none',
          type: '',
          actions: actions
        });
        break;
      }
      case TEST_STATE.LOST:
      case TEST_STATE.CANCELLED:
      case TEST_STATE.FINISHED: {
        result.push({
          label: 'FINISHED',
          labelMessage:
            message === 'stopped'
              ? 'Test has finished and is no longer running'
              : capitalize(message),
          result: 'none',
          type: '',
          actions: this.setDisableAndDefaultAction(['stop'], actions, state, schedule, testType)
        });
        break;
      }
      case TEST_STATE.STOPPING: {
        result.push({
          label: 'STOPPING',
          labelMessage: capitalize(message),
          result: 'warning',
          type: '',
          actions: this.setDisableAndDefaultAction(
            ['stop', 'edit'],
            actions,
            state,
            schedule,
            testType
          )
        });
        break;
      }
      case TEST_STATE.STOPPED: {
        result.push({
          label: 'STOPPED',
          labelMessage: capitalize(message),
          result: 'none',
          type: '',
          actions: this.setDisableAndDefaultAction(['stop'], actions, state, schedule, testType)
        });
        break;
      }
      case TEST_STATE.FAILED: {
        result.push({
          label: 'FAILED',
          labelMessage: capitalize(message),
          result: 'critical',
          type: '',
          actions: this.setDisableAndDefaultAction(['stop'], actions, state, schedule, testType)
        });
        break;
      }
      case TEST_STATE.UNKNOWN: {
        result.push({
          label: 'UNKNOWN',
          labelMessage: capitalize(message),
          result: 'critical',
          type: '',
          actions: actions
        });
        break;
      }
      default: {
        result.push({
          label: 'UNKNOWN',
          labelMessage: 'Unknown',
          result: 'critical',
          type: '',
          actions: actions
        });
      }
    }

    return result;
  }

  getAgentModelLabel(agentType: string): string {
    switch (agentType ? agentType.toLowerCase() : agentType) {
      case 'vta':
        return AGENT_MODEL.VTA;
      case 'vta-ts':
      case 'vtats':
        return AGENT_MODEL.VTA_TS;
      case 'vpma':
        return AGENT_MODEL.VPMA;
      case 'vpma-l':
      case 'vpmal':
        return AGENT_MODEL.VPMA_L;
      case 'vqt':
        return AGENT_MODEL.VQT;
      case 'qt600-10':
      case 'qt60010':
        return AGENT_MODEL.QT600;
      case 'jmep1g':
      case 'jmep-1g':
        return AGENT_MODEL.JMEP_1G;
      case 'jmep10g':
      case 'jmep-10g':
        return AGENT_MODEL.JMEP_10G;
      case 'nsc':
        return AGENT_MODEL.NSC;
      case 'map-2100':
      case 'map2100':
        return AGENT_MODEL.MAP2100;
      case 'tb5800100g':
      case 'tb-5800-100g':
        return AGENT_MODEL.TB_5800_100G;
      case 'tb5800v2':
      case 'tb-5800-v2':
        return AGENT_MODEL.TB_5800v2;
      case 'tb5882':
      case 'tb-5882':
        return AGENT_MODEL.TB_5882;
      case 'tbmts5800100g':
      case 'tb-mts-5800-100g':
        return AGENT_MODEL.TB_MTS_5800_100G;
      case 'tbmts5800v2':
      case 'tb-mts-5800v2':
        return AGENT_MODEL.TB_MTS_5800v2;
      case 'mts5800v2':
      case 'mts-5800-v2':
        return AGENT_MODEL.MTS_5800V2;
      case 'mts5800100g':
      case 'mts-5800-100g':
        return AGENT_MODEL.MTS_5800_100G;
      case 'mts5882':
      case 'mts-5882':
        return AGENT_MODEL.MTS_5882;
      case 'sc4800':
        return AGENT_MODEL.SC4800;
      case 'sc4800P':
        return AGENT_MODEL.SC4800P;
      case 'esam':
        return AGENT_MODEL.ESAM;
      case 'unmanaged':
        return AGENT_MODEL.UNMANAGED;
      case 'vspa':
        return AGENT_MODEL.VSPA;
      case 'vspa-sat-lb':
      case 'vspasatlb':
        return AGENT_MODEL.VSPA_SAT_LB;
      case 'vspa-sat':
      case 'vspasat':
        return AGENT_MODEL.VSPA_SAT;
      case 'vspa-sat-ts':
      case 'vspasatts':
        return AGENT_MODEL.VSPA_SAT_TS;
      case 'vspa-pm':
      case 'vspapm':
        return AGENT_MODEL.VSPA_PM;
      case 'vspa-pm-l':
      case 'vspapml':
        return AGENT_MODEL.VSPA_PM_L;
      default:
        return agentType;
    }
  }

  getTestType(testType: string): string {
    switch (testType) {
      case 'y1564': {
        return TestTypeDisplayValue.Y1564;
      }
      case 'truespeed': {
        return TestTypeDisplayValue.TRUESPEED;
      }
      case 'twamp-initiator': {
        return TestTypeDisplayValue.TWAMP;
      }
      case 'udp': {
        return TestTypeDisplayValue.UDP;
      }
      case 'y1731':
      case 'y1731-initiator': {
        return TestTypeDisplayValue.Y1731;
      }
      case 'rfc2544': {
        return TestTypeDisplayValue.RFC2544;
      }
      case 'network-quality': {
        return TestTypeDisplayValue.NETWORK_QUALITY;
      }
      case 'servicecontinuity': {
        return TestTypeDisplayValue.SERVICE_CONTINUITY;
      }
      case 'voip': {
        return TestTypeDisplayValue.VOIP;
      }
      case 'http': {
        return TestTypeDisplayValue.HTTP;
      }
      case 'ftp': {
        return TestTypeDisplayValue.FTP;
      }
      case 'multistep': {
        return TestTypeDisplayValue.MULTISTEP;
      }
      case 'bert': {
        return TestTypeDisplayValue.BERT;
      }
      default:
        return testType;
    }
  }

  getSchedulingInfo(
    // eslint-disable-next-line
    entity: any
  ): { label: string; sublabel: string; sublabel1: string } {
    return {
      label: this.isScheduledTest(entity)
        ? entity.getTestType() === TestTypeValue.MULTISTEP
          ? this.getMultistepSchedule(entity)
          : this.getSchedule(entity)
        : entity.getTestType() === TestTypeValue.TWAMP ||
          entity.getTestType() === TestTypeValue.TWAMP_LIGHT_REFLECTOR ||
          entity.getTestType() === TestTypeValue.TWAMP_REFLECTOR ||
          entity.getTestType() === TestTypeValue.Y1731
        ? 'Continuous'
        : 'Every 1 min',
      sublabel:
        entity.isRunOnceTest() &&
        (entity.getTestType() === TestTypeValue.SERVICE_CONTINUITY ||
          entity.getTestType() === TestTypeValue.MULTISTEP ||
          entity.getTestType() === TestTypeValue.VOIP)
          ? 'runs only for 8 hours'
          : '',
      sublabel1:
        entity.isRunOnceTest() &&
        entity.getTestType() !== TestTypeValue.TWAMP &&
        entity.getTestType() !== TestTypeValue.Y1731
          ? 'On demand'
          : ''
    };
  }

  isScheduledTest(entity: TestListEntity): boolean {
    return (
      entity.getTestType() === TestTypeValue.MULTISTEP ||
      entity.getTestType() === TestTypeValue.TRUESPEED ||
      entity.getTestType() === TestTypeValue.Y1564 ||
      entity.getTestType() === TestTypeValue.RFC2544 ||
      entity.getTestType() === TestTypeValue.UDP ||
      entity.getTestType() === TestTypeValue.HTTP ||
      entity.getTestType() === TestTypeValue.FTP ||
      entity.getTestType() === TestTypeValue.BERT
    );
  }

  // eslint-disable-next-line
  getSchedule(entity: any): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const repeatList: any[] = [
      { name: 'Run once', value: 0 },
      { name: 'Every 5 minutes', value: 300 },
      { name: 'Every 15 minutes', value: 900 },
      { name: 'Every 30 minutes', value: 1800 },
      { name: 'Every hour', value: 3600 },
      { name: 'Every day', value: 86400 },
      { name: 'Every week', value: 604800 },
      { name: 'Every 30 days', value: 2592000 }
    ];
    const schedulingInterval = entity.getTestListData()?.interval ?? 0;
    const schedulingInfo = repeatList.find(x => x.value === schedulingInterval);
    return schedulingInfo?.name ?? repeatList[0].name;
  }

  getMultistepSchedule(entity: TestListEntity): string {
    const repeatList: any[] = [
      { name: 'Every 5 minutes', value: 300 },
      { name: 'Every 15 minutes', value: 900 },
      { name: 'Every 30 minutes', value: 1800 },
      { name: 'Every hour', value: 3600 },
      { name: 'Every day', value: 86400 },
      { name: 'Every week', value: 604800 }
    ];
    const schedulingInterval = entity.getTestListData()?.interval ?? 300;
    const schedulingInfo = repeatList.find(x => x.value === schedulingInterval);
    return schedulingInfo?.name ?? repeatList[0].name;
  }

  // eslint-disable-next-line
  setDisableAndDefaultAction(
    optionId: string[],
    actions: any,
    state = null,
    schedule = null,
    type: string
  ): any {
    optionId.forEach(ele => {
      const action = find(actions, { id: ele });
      action.disable = true;
    });
    const action = find(actions, { label: ACTION_NAME.VIEW_RESULTS });
    action.isDefaultAction = true;
    //  hide the below actions which applicable for single test workflow as it not applicable for 3.0
    // if (state && schedule && state == 'running' && schedule.label == 'Run once') {
    //   const action = find(actions, { label: ACTION_NAME.VIEW_RUNNING });
    //   action.isDefaultAction = true;
    // } else if (state && schedule && state != 'running' && schedule.label == 'Run once') {
    //   const action = find(actions, { label: ACTION_NAME.VIEW_REPORTS });
    //   action.isDefaultAction = true;
    // } else {
    //   const action = find(actions, { label: ACTION_NAME.VIEW_RESULTS });
    //   action.isDefaultAction = true;
    // }
    return actions;
  }

  getUpdatedDate(entity: TestListEntity): { value: string; label: string; sublabel: string } {
    const result = { value: '', label: '', sublabel: '' };
    const updatedTime = entity.getUpdatedTime();
    result.label = this.dateFormatter.getLocaleDateFromTimestamp(updatedTime);
    result.sublabel = this.dateFormatter.getTimeFromTimestamp(updatedTime);
    result.value = updatedTime;
    return result;
  }

  getExpiryDate(entity: TestListEntity): {
    value: string;
    label: string;
    sublabel: string;
    expired: string;
  } {
    const result = { value: '', label: '', sublabel: '', expired: '' };
    if (entity.getAgentMode() === TestMode.CLIENT_TO_MA) {
      const expirationDate = entity.getExpirationDate();
      if (expirationDate && expirationDate !== '30000101000000') {
        result.label = this.dateFormatter.getLocaleDateFromTimestamp(expirationDate);
        result.sublabel = this.dateFormatter.getTimeFromTimestamp(expirationDate);
        const expiryDate = Date.parse(this.dateFormatter.getISODateString(expirationDate));
        const currentDate = Date.now();
        if (expiryDate < currentDate) {
          result.expired = 'expired';
        }
        result.value = expirationDate;
      }
    }
    return result;
  }
}
